import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Stack,
  Paper,
  Group,
  Text,
  Button,
  Table,
  Badge,
  ActionIcon,
  Tooltip,
  TextInput,
  Loader,
  Pagination,
  Menu,
  Alert
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import {
  IconPlus,
  IconSearch,
  IconTrash,
  IconCheck,
  IconDotsVertical,
  IconEdit,
  IconAlignLeft,
  IconAlertTriangle,
  IconClock,
  IconInfoCircle
} from '@tabler/icons-react';
import { aiKnowledgeApi, AIKnowledgeText } from '@/api/aiKnowledge.api';
import TextEntryModal from './TextEntryModal';

interface TextEntriesTabProps {
  onDataChange: () => void;
}

const TextEntriesTab = ({ onDataChange }: TextEntriesTabProps) => {
  const { t } = useTranslation();
  const [entries, setEntries] = useState<AIKnowledgeText[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 400);
  const [modalOpen, setModalOpen] = useState(false);
  const [editEntry, setEditEntry] = useState<AIKnowledgeText | null>(null);

  const fetchEntries = useCallback(async () => {
    try {
      setLoading(true);
      const response = await aiKnowledgeApi.getTexts({
        page,
        limit: 15,
        search: debouncedSearch || undefined
      });
      if (response.data.success) {
        setEntries(response.data.data.items);
        setTotalPages(response.data.data.pagination.totalPages);
      }
    } catch (error) {
      console.error('Failed to fetch text entries:', error);
      notifications.show({
        title: t('common.error'),
        message: t('aiKnowledge.errors.loadFailed'),
        color: 'red'
      });
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const handleCreate = () => {
    setEditEntry(null);
    setModalOpen(true);
  };

  const handleEdit = async (entry: AIKnowledgeText) => {
    try {
      const response = await aiKnowledgeApi.getText(entry.id);
      if (response.data.success) {
        setEditEntry(response.data.data);
        setModalOpen(true);
      }
    } catch (error) {
      console.error('Failed to load text entry:', error);
    }
  };

  const handleDelete = (entry: AIKnowledgeText) => {
    modals.openConfirmModal({
      title: t('aiKnowledge.texts.deleteTitle'),
      children: (
        <Text size="sm">
          {t('aiKnowledge.texts.deleteConfirm', { name: entry.title })}
        </Text>
      ),
      labels: {
        confirm: t('common.delete'),
        cancel: t('common.cancel')
      },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await aiKnowledgeApi.deleteText(entry.id);
          notifications.show({
            title: t('common.success'),
            message: t('aiKnowledge.texts.deleted'),
            color: 'green',
            icon: <IconCheck size={18} />
          });
          fetchEntries();
          onDataChange();
        } catch (error) {
          notifications.show({
            title: t('common.error'),
            message: t('aiKnowledge.errors.deleteFailed'),
            color: 'red'
          });
        }
      }
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'indexed':
        return <Badge color="green" variant="light" size="sm" leftSection={<IconCheck size={12} />}>{t('aiKnowledge.status.indexed')}</Badge>;
      case 'error':
        return <Badge color="red" variant="light" size="sm" leftSection={<IconAlertTriangle size={12} />}>{t('aiKnowledge.status.error')}</Badge>;
      case 'pending':
        return <Badge color="gray" variant="light" size="sm" leftSection={<IconClock size={12} />}>{t('aiKnowledge.status.pending')}</Badge>;
      default:
        return <Badge variant="light" size="sm">{status}</Badge>;
    }
  };

  return (
    <Stack gap="md">
      {/* Info */}
      <Alert variant="light" color="violet" icon={<IconInfoCircle size={18} />}>
        <Text size="sm">{t('aiKnowledge.texts.description')}</Text>
      </Alert>

      {/* Header */}
      <Paper p="md" radius="md" withBorder>
        <Group justify="space-between">
          <TextInput
            placeholder={t('aiKnowledge.texts.searchPlaceholder')}
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            w={300}
          />
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={handleCreate}
            variant="gradient"
            gradient={{ from: 'violet', to: 'blue' }}
          >
            {t('aiKnowledge.texts.create')}
          </Button>
        </Group>
      </Paper>

      {/* Table */}
      {loading ? (
        <Group justify="center" py="xl"><Loader /></Group>
      ) : entries.length === 0 ? (
        <Paper p="xl" radius="md" withBorder>
          <Stack align="center" gap="md">
            <IconAlignLeft size={48} color="var(--mantine-color-dimmed)" />
            {search ? (
              <Text c="dimmed" ta="center">{t('aiKnowledge.texts.noSearchResults')}</Text>
            ) : (
              <>
                <Text c="dimmed" ta="center">{t('aiKnowledge.texts.noTexts')}</Text>
                <Text size="sm" c="dimmed" ta="center" maw={500}>
                  {t('aiKnowledge.texts.noTextsHint')}
                </Text>
                <Button
                  leftSection={<IconPlus size={16} />}
                  onClick={handleCreate}
                  variant="light"
                >
                  {t('aiKnowledge.texts.createFirst')}
                </Button>
              </>
            )}
          </Stack>
        </Paper>
      ) : (
        <Paper radius="md" withBorder style={{ overflow: 'hidden' }}>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('aiKnowledge.texts.columns.title')}</Table.Th>
                <Table.Th>{t('aiKnowledge.texts.columns.category')}</Table.Th>
                <Table.Th>{t('aiKnowledge.texts.columns.preview')}</Table.Th>
                <Table.Th>{t('aiKnowledge.texts.columns.chunks')}</Table.Th>
                <Table.Th>{t('aiKnowledge.texts.columns.status')}</Table.Th>
                <Table.Th>{t('aiKnowledge.texts.columns.createdAt')}</Table.Th>
                <Table.Th w={80}>{t('aiKnowledge.texts.columns.actions')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {entries.map((entry) => (
                <Table.Tr key={entry.id}>
                  <Table.Td>
                    <Text size="sm" fw={500} lineClamp={1}>{entry.title}</Text>
                  </Table.Td>
                  <Table.Td>
                    {entry.category ? (
                      <Badge variant="light" size="sm">{entry.category}</Badge>
                    ) : (
                      <Text size="sm" c="dimmed">—</Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed" lineClamp={2} maw={250}>
                      {entry.content}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{entry.chunks_count}</Text>
                  </Table.Td>
                  <Table.Td>
                    {getStatusBadge(entry.status)}
                    {entry.status === 'error' && entry.error_message && (
                      <Tooltip label={entry.error_message} multiline w={300}>
                        <IconAlertTriangle size={14} color="var(--mantine-color-red-5)" style={{ marginLeft: 4, cursor: 'help' }} />
                      </Tooltip>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {new Date(entry.created_at).toLocaleDateString()}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Menu shadow="md" width={180} position="bottom-end">
                      <Menu.Target>
                        <ActionIcon variant="subtle" color="gray">
                          <IconDotsVertical size={16} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item
                          leftSection={<IconEdit size={14} />}
                          onClick={() => handleEdit(entry)}
                        >
                          {t('common.edit')}
                        </Menu.Item>
                        <Menu.Divider />
                        <Menu.Item
                          leftSection={<IconTrash size={14} />}
                          color="red"
                          onClick={() => handleDelete(entry)}
                        >
                          {t('common.delete')}
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Group justify="center">
          <Pagination value={page} onChange={setPage} total={totalPages} />
        </Group>
      )}

      {/* Create/Edit Modal */}
      <TextEntryModal
        opened={modalOpen}
        onClose={() => { setModalOpen(false); setEditEntry(null); }}
        onSuccess={() => {
          setModalOpen(false);
          setEditEntry(null);
          fetchEntries();
          onDataChange();
        }}
        editEntry={editEntry}
      />
    </Stack>
  );
};

export default TextEntriesTab;
